/* eslint-disable rover/no-platform-specific-globals-or-imports */
import { t } from '@lingui/macro';

import addressFactory from '@rover/react-lib/src/factories/addressFactory';
import { camelize } from '@rover/rsdk/src/modules/Network/object-utilities';
import type { Address, LatLng } from '@rover/types';

import APIError from './APIError';

export const RESOLVE_LOCATION_ERROR_MESSAGE = (): string =>
  t`Something went wrong resolving this location.`;

export const LOAD_LOCATION_RESULTS_ERROR_MESSAGE = (): string =>
  t`Something went wrong loading location results.`;

const DISALLOWED_COMPONENT_TYPES = new Set(['political']);

export const geocoderResponseToLatLng = ({ geometry }: Record<string, any>): LatLng | null => {
  if (!geometry) return null;
  const lat = geometry.location.lat();
  const lng = geometry.location.lng();
  return {
    lat,
    lng,
  };
};
export const geocoderResponseToAddressObject = ({
  addressComponents,
}: Record<string, any>): Address | null => {
  if (!addressComponents) return null;
  // The geocoding API response gives each address element's type (e.g. street_number, country) as a list
  // next to the value. We want to convert that into an Object, where each value has a single type as the key.
  // https://developers.google.com/maps/documentation/geocoding/intro
  const address = {} as any;
  addressComponents.forEach((component) => {
    // Reduce the list of types to a single descriptive type.
    // Skip over some component types in the geocoding API response that are too generic, inspecific, or are reused
    const componentType = component.types.reduce((bestDescriptiveType, currentType) =>
      DISALLOWED_COMPONENT_TYPES.has(bestDescriptiveType) ? currentType : bestDescriptiveType
    );
    address[componentType] = component.short_name;
  });
  return addressFactory({
    addressLine1: `${address.street_number} ${address.route}`,
    addressLine2: address.subpremise,
    city: address.locality || address.sublocality,
    state: address.administrative_area_level_1,
    countryCode: address.country,
    zip: address.postal_code,
  });
};
export const getCurrentPosition = (): Promise<any> =>
  new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(
      (position) => resolve(position),
      () => reject(new Error(RESOLVE_LOCATION_ERROR_MESSAGE())),
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }
    );
  });
export const getLocationAutocompleteResults = async (query: string): Promise<string[]> =>
  new Promise((resolve, reject) => {
    const sessionToken = new window.google.maps.places.AutocompleteSessionToken();
    new window.google.maps.places.AutocompleteService().getPlacePredictions(
      {
        input: query || {},
        types: ['address'],
        sessionToken,
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          resolve(predictions!.map((p) => p.description));
          return;
        }

        if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          resolve([]);
          return;
        }
        // @ts-expect-error
        reject(new APIError(LOAD_LOCATION_RESULTS_ERROR_MESSAGE(), status));
      },
      // @ts-expect-error: don't want to break behavior
      () => reject(new Error(LOAD_LOCATION_RESULTS_ERROR_MESSAGE()))
    );
  });

export const geocode = async (geocodable: any): Promise<Record<string, any>> =>
  new Promise((resolve, reject) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode(
      geocodable,
      (results) => {
        if (results) {
          // look for "street_address" type results
          const streetAddressResult = results.find((result) =>
            result.types.includes('street_address')
          );
          // if found, resolve with the street address result, otherwise use the first result
          resolve(camelize(streetAddressResult || results[0]));
        } else {
          reject(new Error(RESOLVE_LOCATION_ERROR_MESSAGE()));
        }
      },
      // @ts-expect-error: don't want to break behavior
      () => {
        reject(new Error(RESOLVE_LOCATION_ERROR_MESSAGE()));
      },
      {
        enableHighAccuracy: true,
        timeout: 5000,
        maximumAge: 0,
      }
    );
  });

export const getLatLngFromAddressByGeocode = async (
  address?: string,
  region?: string
): Promise<LatLng | null> =>
  geocoderResponseToLatLng(
    await geocode({
      address,
      region,
    })
  );
export const getGeocodedAddressFromString = async (address: string): Promise<Address | null> =>
  geocoderResponseToAddressObject(
    await geocode({
      address,
    })
  );
export const getGeocodedAddressFromLatLng = async ({
  latitude,
  longitude,
}: {
  latitude: number;
  longitude: number;
}): Promise<Address | null> => {
  const latLng = new window.google.maps.LatLng(latitude, longitude);
  return geocoderResponseToAddressObject(
    await geocode({
      latLng,
    })
  );
};

export const getFormattedAddressFromLatLng = async ({
  latitude,
  longitude,
}: {
  latitude: number;
  longitude: number;
}): Promise<string> => {
  const latLng = new window.google.maps.LatLng(latitude, longitude);

  const { formattedAddress } = await geocode({
    latLng,
  });

  return formattedAddress;
};
